import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
  Card,
  CardContent,
  Alert,
  Divider,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccountBalance as AccountBalanceIcon,
  Assessment as AssessmentIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  Sync as SyncIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  AttachMoney as AttachMoneyIcon,
  Description as DescriptionIcon,
  AccountTree as AccountTreeIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CompareArrows as CompareArrowsIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AccountingPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const [loading, setLoading] = useState(false);
  const [accountingMethod, setAccountingMethod] = useState<'cash' | 'accrual'>('accrual');
  
  // Chart of Accounts state
  const [accounts, setAccounts] = useState<any[]>([]);
  const [accountDialog, setAccountDialog] = useState(false);
  const [newAccount, setNewAccount] = useState({
    accountNumber: '',
    accountName: '',
    accountType: '',
    accountSubtype: '',
    description: ''
  });

  // Journal Entry state
  const [journalEntries, setJournalEntries] = useState<any[]>([]);
  const [journalDialog, setJournalDialog] = useState(false);
  const [journalEntry, setJournalEntry] = useState({
    entryDate: new Date(),
    description: '',
    locationId: '',
    lines: [{ accountId: '', debit: 0, credit: 0, memo: '' }]
  });

  // General Ledger state
  const [generalLedger, setGeneralLedger] = useState<any[]>([]);
  const [selectedAccount, setSelectedAccount] = useState('');

  // Financial Statements state
  const [incomeStatement, setIncomeStatement] = useState<any>(null);
  const [balanceSheet, setBalanceSheet] = useState<any>(null);
  const [statementDates, setStatementDates] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    endDate: new Date()
  });

  // Periods state
  const [periods, setPeriods] = useState<any[]>([]);
  const [trialBalance, setTrialBalance] = useState<any>(null);

  // Reconciliation state
  const [reconciliations, setReconciliations] = useState<any[]>([]);
  const [reconciliationDialog, setReconciliationDialog] = useState(false);

  // Settings state
  const [settings, setSettings] = useState<any>(null);
  const [locations, setLocations] = useState<any[]>([]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    await Promise.all([
      fetchChartOfAccounts(),
      fetchJournalEntries(),
      fetchPeriods(),
      fetchLocations()
    ]);
  };

  const fetchChartOfAccounts = async () => {
    try {
      const response = await axios.get('/api/accounting/chart-of-accounts', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccounts(Array.isArray(response.data) ? response.data : response.data.accounts || []);
    } catch (error) {
      console.error('Error fetching chart of accounts:', error);
    }
  };

  const fetchJournalEntries = async () => {
    try {
      const response = await axios.get('/api/accounting/journal-entries', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJournalEntries(Array.isArray(response.data) ? response.data : response.data.entries || []);
    } catch (error) {
      console.error('Error fetching journal entries:', error);
    }
  };

  const fetchGeneralLedger = async (accountId?: string) => {
    try {
      const params = accountId ? { accountId } : {};
      const response = await axios.get('/api/accounting/general-ledger', {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setGeneralLedger(Array.isArray(response.data) ? response.data : response.data.ledger || []);
    } catch (error) {
      console.error('Error fetching general ledger:', error);
    }
  };

  const fetchPeriods = async () => {
    try {
      const response = await axios.get('/api/accounting/periods', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPeriods(Array.isArray(response.data) ? response.data : response.data.periods || []);
    } catch (error) {
      console.error('Error fetching periods:', error);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get('/api/locations', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocations(Array.isArray(response.data) ? response.data : response.data.locations || []);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    
    // Load specific data for tabs
    if (newValue === 2) fetchGeneralLedger();
    if (newValue === 3) generateFinancialStatements();
  };

  const createAccount = async () => {
    try {
      await axios.post('/api/accounting/chart-of-accounts', newAccount, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccountDialog(false);
      fetchChartOfAccounts();
    } catch (error) {
      console.error('Error creating account:', error);
    }
  };

  const createJournalEntry = async () => {
    try {
      await axios.post('/api/accounting/journal-entries', {
        ...journalEntry,
        accountingMethod
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJournalDialog(false);
      fetchJournalEntries();
    } catch (error) {
      console.error('Error creating journal entry:', error);
    }
  };

  const approveJournalEntry = async (id: string) => {
    try {
      await axios.put(`/api/accounting/journal-entries/${id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchJournalEntries();
    } catch (error) {
      console.error('Error approving journal entry:', error);
    }
  };

  const reverseJournalEntry = async (id: string) => {
    try {
      await axios.post(`/api/accounting/journal-entries/${id}/reverse`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchJournalEntries();
    } catch (error) {
      console.error('Error reversing journal entry:', error);
    }
  };

  const generateFinancialStatements = async () => {
    setLoading(true);
    try {
      const [incomeRes, balanceRes] = await Promise.all([
        axios.get('/api/accounting/income-statement', {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            startDate: statementDates.startDate,
            endDate: statementDates.endDate,
            accountingMethod
          }
        }),
        axios.get('/api/accounting/balance-sheet', {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            asOfDate: statementDates.endDate,
            accountingMethod
          }
        })
      ]);
      
      setIncomeStatement(incomeRes.data);
      setBalanceSheet(balanceRes.data);
    } catch (error) {
      console.error('Error generating statements:', error);
    } finally {
      setLoading(false);
    }
  };

  const closePeriod = async (year: number, month: number) => {
    try {
      await axios.post(`/api/accounting/periods/${year}/${month}/close`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchPeriods();
    } catch (error) {
      console.error('Error closing period:', error);
    }
  };

  const generateTrialBalance = async (periodId: string) => {
    try {
      const response = await axios.post('/api/accounting/trial-balance/generate', {
        periodId,
        accountingMethod
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrialBalance(response.data || {});
    } catch (error) {
      console.error('Error generating trial balance:', error);
    }
  };

  const initializeChartOfAccounts = async (locationId: string) => {
    try {
      await axios.post(`/api/accounting/chart-of-accounts/initialize/${locationId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchChartOfAccounts();
    } catch (error) {
      console.error('Error initializing chart of accounts:', error);
    }
  };

  const addJournalLine = () => {
    setJournalEntry({
      ...journalEntry,
      lines: [...journalEntry.lines, { accountId: '', debit: 0, credit: 0, memo: '' }]
    });
  };

  const updateJournalLine = (index: number, field: string, value: any) => {
    const newLines = [...journalEntry.lines];
    newLines[index] = { ...newLines[index], [field]: value };
    setJournalEntry({ ...journalEntry, lines: newLines });
  };

  const removeJournalLine = (index: number) => {
    const newLines = journalEntry.lines.filter((_, i) => i !== index);
    setJournalEntry({ ...journalEntry, lines: newLines });
  };

  const getTotalDebits = () => {
    return journalEntry.lines.reduce((sum, line) => sum + (parseFloat(line.debit.toString()) || 0), 0);
  };

  const getTotalCredits = () => {
    return journalEntry.lines.reduce((sum, line) => sum + (parseFloat(line.credit.toString()) || 0), 0);
  };

  const isJournalBalanced = () => {
    return Math.abs(getTotalDebits() - getTotalCredits()) < 0.01;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">Accounting Management</Typography>
          <ToggleButtonGroup
            value={accountingMethod}
            exclusive
            onChange={(e, value) => value && setAccountingMethod(value)}
            size="small"
          >
            <ToggleButton value="cash">Cash Basis</ToggleButton>
            <ToggleButton value="accrual">Accrual Basis</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Paper sx={{ mb: 3 }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab icon={<AccountTreeIcon />} label="Chart of Accounts" />
            <Tab icon={<ReceiptIcon />} label="Journal Entries" />
            <Tab icon={<AccountBalanceIcon />} label="General Ledger" />
            <Tab icon={<AssessmentIcon />} label="Financial Statements" />
            <Tab icon={<CompareArrowsIcon />} label="Period Management" />
            <Tab icon={<SyncIcon />} label="Reconciliation" />
            <Tab icon={<DescriptionIcon />} label="Settings" />
          </Tabs>
        </Paper>

        {/* Chart of Accounts Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Chart of Accounts</Typography>
            <Box>
              {locations.length > 0 && accounts.length === 0 && (
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => initializeChartOfAccounts(locations[0]._id)}
                  sx={{ mr: 1 }}
                >
                  Initialize Default Accounts
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAccountDialog(true)}
              >
                Add Account
              </Button>
            </Box>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Account Number</TableCell>
                  <TableCell>Account Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Subtype</TableCell>
                  <TableCell>Normal Balance</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(accounts || []).map((account) => (
                  <TableRow key={account._id}>
                    <TableCell>{account.accountNumber}</TableCell>
                    <TableCell>{account.accountName}</TableCell>
                    <TableCell>
                      <Chip 
                        label={account.accountType} 
                        size="small"
                        color={account.accountType === 'asset' ? 'success' : 
                               account.accountType === 'liability' ? 'error' :
                               account.accountType === 'equity' ? 'primary' :
                               account.accountType === 'revenue' ? 'info' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>{account.accountSubtype.replace(/_/g, ' ')}</TableCell>
                    <TableCell>{account.normalBalance}</TableCell>
                    <TableCell>
                      <Chip 
                        label={account.isActive ? 'Active' : 'Inactive'} 
                        size="small"
                        color={account.isActive ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small">
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Journal Entries Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Journal Entries</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setJournalDialog(true)}
            >
              Create Entry
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Entry Number</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {journalEntries.map((entry) => (
                  <TableRow key={entry._id}>
                    <TableCell>{entry.entryNumber}</TableCell>
                    <TableCell>{format(new Date(entry.entryDate), 'MM/dd/yyyy')}</TableCell>
                    <TableCell>{entry.description}</TableCell>
                    <TableCell>
                      <Chip 
                        label={entry.status} 
                        size="small"
                        color={entry.status === 'posted' ? 'success' : 
                               entry.status === 'approved' ? 'info' :
                               entry.status === 'pending' ? 'warning' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{entry.accountingMethod}</TableCell>
                    <TableCell>{entry.createdBy?.name || 'System'}</TableCell>
                    <TableCell>
                      {entry.status === 'pending' && (
                        <Tooltip title="Approve">
                          <IconButton 
                            size="small" 
                            color="success"
                            onClick={() => approveJournalEntry(entry._id)}
                          >
                            <CheckCircleIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {entry.status === 'posted' && (
                        <Tooltip title="Reverse">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => reverseJournalEntry(entry._id)}
                          >
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="View Details">
                        <IconButton size="small">
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* General Ledger Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>General Ledger</Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Account</InputLabel>
              <Select
                value={selectedAccount}
                onChange={(e) => {
                  setSelectedAccount(e.target.value);
                  fetchGeneralLedger(e.target.value);
                }}
              >
                <MenuItem value="">All Accounts</MenuItem>
                {(accounts || []).map((account) => (
                  <MenuItem key={account._id} value={account._id}>
                    {account.accountNumber} - {account.accountName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Account</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell align="right">Debit</TableCell>
                  <TableCell align="right">Credit</TableCell>
                  <TableCell align="right">Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {generalLedger.map((entry) => (
                  <TableRow key={entry._id}>
                    <TableCell>{format(new Date(entry.transactionDate), 'MM/dd/yyyy')}</TableCell>
                    <TableCell>{entry.accountId?.accountName}</TableCell>
                    <TableCell>{entry.description}</TableCell>
                    <TableCell>{entry.reference || '-'}</TableCell>
                    <TableCell align="right">
                      {entry.debit > 0 ? `$${entry.debit.toFixed(2)}` : '-'}
                    </TableCell>
                    <TableCell align="right">
                      {entry.credit > 0 ? `$${entry.credit.toFixed(2)}` : '-'}
                    </TableCell>
                    <TableCell align="right">${entry.balance.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Financial Statements Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>Financial Statements</Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={4}>
                <DatePicker
                  label="Start Date"
                  value={statementDates.startDate}
                  onChange={(date) => date && setStatementDates({ ...statementDates, startDate: date })}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <DatePicker
                  label="End Date"
                  value={statementDates.endDate}
                  onChange={(date) => date && setStatementDates({ ...statementDates, endDate: date })}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  variant="contained"
                  fullWidth
                  sx={{ height: '100%' }}
                  onClick={generateFinancialStatements}
                  disabled={loading}
                >
                  Generate Statements
                </Button>
              </Grid>
            </Grid>
          </Box>

          {loading && <LinearProgress sx={{ mb: 2 }} />}

          {incomeStatement && (
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Income Statement ({accountingMethod} basis)
              </Typography>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {format(new Date(statementDates.startDate), 'MMM dd, yyyy')} - 
                {format(new Date(statementDates.endDate), 'MMM dd, yyyy')}
              </Typography>
              
              {(incomeStatement.sections || []).map((section: any) => (
                <Box key={section.name} sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">{section.name}</Typography>
                  {(section.accounts || []).map((account: any) => (
                    <Box key={account.accountNumber} sx={{ display: 'flex', justifyContent: 'space-between', ml: 2 }}>
                      <Typography>{account.accountName}</Typography>
                      <Typography>${account.amount.toFixed(2)}</Typography>
                    </Box>
                  ))}
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', ml: 2 }}>
                    <Typography fontWeight="bold">Total {section.name}</Typography>
                    <Typography fontWeight="bold">${section.subtotal.toFixed(2)}</Typography>
                  </Box>
                </Box>
              ))}
              
              <Box sx={{ mt: 3, pt: 2, borderTop: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6">Net Income</Typography>
                  <Typography variant="h6" color={incomeStatement.netIncome >= 0 ? 'success.main' : 'error.main'}>
                    ${incomeStatement.netIncome.toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          )}

          {balanceSheet && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Balance Sheet ({accountingMethod} basis)
              </Typography>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                As of {format(new Date(statementDates.endDate), 'MMM dd, yyyy')}
              </Typography>
              
              {(balanceSheet.sections || []).map((section: any) => (
                <Box key={section.name} sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">{section.name}</Typography>
                  {(section.accounts || []).map((account: any) => (
                    <Box key={account.accountNumber} sx={{ display: 'flex', justifyContent: 'space-between', ml: 2 }}>
                      <Typography>{account.accountName}</Typography>
                      <Typography>${account.amount.toFixed(2)}</Typography>
                    </Box>
                  ))}
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', ml: 2 }}>
                    <Typography fontWeight="bold">Total {section.name}</Typography>
                    <Typography fontWeight="bold">${section.subtotal.toFixed(2)}</Typography>
                  </Box>
                </Box>
              ))}
              
              <Box sx={{ mt: 3, pt: 2, borderTop: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6">Total Assets</Typography>
                  <Typography variant="h6">${balanceSheet.totalAssets.toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6">Total Liabilities & Equity</Typography>
                  <Typography variant="h6">${balanceSheet.totalLiabilitiesAndEquity.toFixed(2)}</Typography>
                </Box>
              </Box>
            </Paper>
          )}
        </TabPanel>

        {/* Period Management Tab */}
        <TabPanel value={tabValue} index={4}>
          <Typography variant="h6" gutterBottom>Accounting Periods</Typography>
          
          <Grid container spacing={2}>
            {(periods || []).map((period) => (
              <Grid item xs={12} md={4} key={period._id}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">
                      {format(new Date(period.year, period.month - 1), 'MMMM yyyy')}
                    </Typography>
                    <Chip 
                      label={period.status} 
                      size="small"
                      color={period.status === 'open' ? 'success' : 
                             period.status === 'closed' ? 'warning' : 'error'}
                      sx={{ my: 1 }}
                    />
                    {period.status === 'open' && (
                      <Box sx={{ mt: 2 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          fullWidth
                          onClick={() => closePeriod(period.year, period.month)}
                          startIcon={<LockIcon />}
                        >
                          Close Period
                        </Button>
                      </Box>
                    )}
                    {period.status !== 'open' && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" display="block">
                          Closed by: {period.closedBy?.name}
                        </Typography>
                        <Typography variant="caption" display="block">
                          Closed at: {format(new Date(period.closedAt), 'MM/dd/yyyy')}
                        </Typography>
                      </Box>
                    )}
                    <Button
                      size="small"
                      sx={{ mt: 1 }}
                      onClick={() => generateTrialBalance(period._id)}
                    >
                      Generate Trial Balance
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {trialBalance && (
            <Paper sx={{ mt: 3, p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Trial Balance
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Account</TableCell>
                      <TableCell align="right">Debit</TableCell>
                      <TableCell align="right">Credit</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {trialBalance.accounts.map((account: any) => (
                      <TableRow key={account.accountId}>
                        <TableCell>{account.accountNumber} - {account.accountName}</TableCell>
                        <TableCell align="right">
                          {account.debitBalance > 0 ? `$${account.debitBalance.toFixed(2)}` : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {account.creditBalance > 0 ? `$${account.creditBalance.toFixed(2)}` : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell><strong>Totals</strong></TableCell>
                      <TableCell align="right"><strong>${trialBalance.totalDebits.toFixed(2)}</strong></TableCell>
                      <TableCell align="right"><strong>${trialBalance.totalCredits.toFixed(2)}</strong></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              {!trialBalance.isBalanced && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  Trial balance is not balanced! Difference: ${Math.abs(trialBalance.totalDebits - trialBalance.totalCredits).toFixed(2)}
                </Alert>
              )}
            </Paper>
          )}
        </TabPanel>

        {/* Reconciliation Tab */}
        <TabPanel value={tabValue} index={5}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Account Reconciliation</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setReconciliationDialog(true)}
            >
              Start Reconciliation
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Account</TableCell>
                  <TableCell>Statement Date</TableCell>
                  <TableCell>Statement Balance</TableCell>
                  <TableCell>Cleared Balance</TableCell>
                  <TableCell>Difference</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reconciliations.map((recon) => (
                  <TableRow key={recon._id}>
                    <TableCell>{recon.accountId?.accountName}</TableCell>
                    <TableCell>{format(new Date(recon.statementDate), 'MM/dd/yyyy')}</TableCell>
                    <TableCell>${recon.statementBalance.toFixed(2)}</TableCell>
                    <TableCell>${recon.clearedBalance.toFixed(2)}</TableCell>
                    <TableCell>
                      <Typography color={Math.abs(recon.difference) < 0.01 ? 'success.main' : 'error.main'}>
                        ${recon.difference.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={recon.status} 
                        size="small"
                        color={recon.status === 'completed' ? 'success' : 'warning'}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small">
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Settings Tab */}
        <TabPanel value={tabValue} index={6}>
          <Typography variant="h6" gutterBottom>Accounting Settings</Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Configure accounting preferences and default accounts for each location.
          </Alert>
          
          <Grid container spacing={3}>
            {locations.map((location) => (
              <Grid item xs={12} md={6} key={location._id}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    {location.name}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      // Load settings for this location
                      axios.get(`/api/accounting/settings/${location._id}`, {
                        headers: { Authorization: `Bearer ${token}` }
                      }).then(res => setSettings(res.data));
                    }}
                  >
                    Configure Settings
                  </Button>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        {/* Account Dialog */}
        <Dialog open={accountDialog} onClose={() => setAccountDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Create New Account</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Account Number"
                  value={newAccount.accountNumber}
                  onChange={(e) => setNewAccount({ ...newAccount, accountNumber: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Account Name"
                  value={newAccount.accountName}
                  onChange={(e) => setNewAccount({ ...newAccount, accountName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Account Type</InputLabel>
                  <Select
                    value={newAccount.accountType}
                    onChange={(e) => setNewAccount({ ...newAccount, accountType: e.target.value })}
                  >
                    <MenuItem value="asset">Asset</MenuItem>
                    <MenuItem value="liability">Liability</MenuItem>
                    <MenuItem value="equity">Equity</MenuItem>
                    <MenuItem value="revenue">Revenue</MenuItem>
                    <MenuItem value="expense">Expense</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Account Subtype</InputLabel>
                  <Select
                    value={newAccount.accountSubtype}
                    onChange={(e) => setNewAccount({ ...newAccount, accountSubtype: e.target.value })}
                    disabled={!newAccount.accountType}
                  >
                    {newAccount.accountType === 'asset' && [
                      <MenuItem key="current_asset" value="current_asset">Current Asset</MenuItem>,
                      <MenuItem key="fixed_asset" value="fixed_asset">Fixed Asset</MenuItem>,
                      <MenuItem key="other_asset" value="other_asset">Other Asset</MenuItem>
                    ]}
                    {newAccount.accountType === 'liability' && [
                      <MenuItem key="current_liability" value="current_liability">Current Liability</MenuItem>,
                      <MenuItem key="long_term_liability" value="long_term_liability">Long Term Liability</MenuItem>
                    ]}
                    {newAccount.accountType === 'equity' && [
                      <MenuItem key="owners_equity" value="owners_equity">Owner's Equity</MenuItem>,
                      <MenuItem key="retained_earnings" value="retained_earnings">Retained Earnings</MenuItem>
                    ]}
                    {newAccount.accountType === 'revenue' && [
                      <MenuItem key="operating_revenue" value="operating_revenue">Operating Revenue</MenuItem>,
                      <MenuItem key="other_revenue" value="other_revenue">Other Revenue</MenuItem>
                    ]}
                    {newAccount.accountType === 'expense' && [
                      <MenuItem key="cost_of_goods_sold" value="cost_of_goods_sold">Cost of Goods Sold</MenuItem>,
                      <MenuItem key="operating_expense" value="operating_expense">Operating Expense</MenuItem>,
                      <MenuItem key="other_expense" value="other_expense">Other Expense</MenuItem>
                    ]}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Description"
                  value={newAccount.description}
                  onChange={(e) => setNewAccount({ ...newAccount, description: e.target.value })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAccountDialog(false)}>Cancel</Button>
            <Button 
              onClick={createAccount} 
              variant="contained"
              disabled={!newAccount.accountNumber || !newAccount.accountName || !newAccount.accountType || !newAccount.accountSubtype}
            >
              Create
            </Button>
          </DialogActions>
        </Dialog>

        {/* Journal Entry Dialog */}
        <Dialog open={journalDialog} onClose={() => setJournalDialog(false)} maxWidth="lg" fullWidth>
          <DialogTitle>Create Journal Entry</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={4}>
                <DatePicker
                  label="Entry Date"
                  value={journalEntry.entryDate}
                  onChange={(date) => date && setJournalEntry({ ...journalEntry, entryDate: date })}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Location</InputLabel>
                  <Select
                    value={journalEntry.locationId}
                    onChange={(e) => setJournalEntry({ ...journalEntry, locationId: e.target.value })}
                  >
                    {locations.map((location) => (
                      <MenuItem key={location._id} value={location._id}>
                        {location.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Description"
                  value={journalEntry.description}
                  onChange={(e) => setJournalEntry({ ...journalEntry, description: e.target.value })}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle1">Journal Lines</Typography>
                <Button startIcon={<AddIcon />} onClick={addJournalLine}>
                  Add Line
                </Button>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Account</TableCell>
                      <TableCell>Debit</TableCell>
                      <TableCell>Credit</TableCell>
                      <TableCell>Memo</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {journalEntry.lines.map((line, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <FormControl fullWidth size="small">
                            <Select
                              value={line.accountId}
                              onChange={(e) => updateJournalLine(index, 'accountId', e.target.value)}
                            >
                              {(accounts || []).map((account) => (
                                <MenuItem key={account._id} value={account._id}>
                                  {account.accountNumber} - {account.accountName}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={line.debit}
                            onChange={(e) => updateJournalLine(index, 'debit', parseFloat(e.target.value) || 0)}
                            InputProps={{
                              startAdornment: '$'
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={line.credit}
                            onChange={(e) => updateJournalLine(index, 'credit', parseFloat(e.target.value) || 0)}
                            InputProps={{
                              startAdornment: '$'
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={line.memo}
                            onChange={(e) => updateJournalLine(index, 'memo', e.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton 
                            size="small" 
                            onClick={() => removeJournalLine(index)}
                            disabled={journalEntry.lines.length === 1}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell><strong>Totals</strong></TableCell>
                      <TableCell><strong>${getTotalDebits().toFixed(2)}</strong></TableCell>
                      <TableCell><strong>${getTotalCredits().toFixed(2)}</strong></TableCell>
                      <TableCell colSpan={2}>
                        {!isJournalBalanced() && (
                          <Alert severity="error" sx={{ py: 0 }}>
                            Entry must balance
                          </Alert>
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setJournalDialog(false)}>Cancel</Button>
            <Button 
              onClick={createJournalEntry} 
              variant="contained"
              disabled={!isJournalBalanced() || !journalEntry.description || !journalEntry.locationId}
            >
              Create Entry
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default AccountingPage;